package dk.smark.demo.ui

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AccountCircle
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonColors
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.zIndex
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import demo.composeapp.generated.resources.Res
import demo.composeapp.generated.resources.compose_multiplatform
import dk.smark.demo.FeatureFlags
import dk.smark.demo.Greeting
import dk.smark.demo.MainViewModel
import dk.smark.demo.location.GenericLocation
import dk.smark.demo.location.LocationResult
import dk.smark.demo.location.createLocationService
import dk.smark.demo.location.createShareService
import dk.smark.demo.map.MapController
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun HomeScreen(
    viewModel: MainViewModel = koinViewModel(),
    mapComponent: @Composable (location: GenericLocation) -> Unit,
    onUserLocationFound: (lat: Double, lon: Double) -> Unit,
    onTrackAvailable: (track: List<GenericLocation>) -> Unit,
    bannerAd: @Composable () -> Unit,
    onProfileClick: () -> Unit = {},
) {

    val shareService = remember { createShareService() }

    val isAdFree by viewModel.isAdFree.collectAsStateWithLifecycle(initialValue = null)

    val location by viewModel.lastKnownLocation.collectAsState(initial = null)

    //val track by viewModel.locationTrack.collectAsState()

    var hasLocationPermission by remember {
        mutableStateOf(
            true
        )
    }

    LaunchedEffect(Unit) {
        // viewModel.fetchLocation()
        viewModel.startTracking()
    }

    HomeScreenContent(
        hasLocationPermission = hasLocationPermission,
        location = location,
        mapComponent = mapComponent,
        onProfileClick = onProfileClick,
        shareServiceIsSupported = { shareService.isSupported() },
        shareLocation = { viewModel.shareLocation() },
        onUserLocationFound = onUserLocationFound,
        track = listOf(),
        onTrackAvailable = onTrackAvailable,
    )
}

@Composable
fun HomeScreenContent(
    hasLocationPermission: Boolean,
    location: GenericLocation? = null,
    mapComponent: @Composable (location: GenericLocation) -> Unit,
    onProfileClick: () -> Unit = {},
    shareServiceIsSupported: () -> Boolean = { false },
    shareLocation: () -> Unit = {},
    onUserLocationFound: (lat: Double, lon: Double) -> Unit,
    track: List<GenericLocation>,
    onTrackAvailable: (track: List<GenericLocation>) -> Unit,
) {
    Box(modifier = Modifier.fillMaxSize()) {
        Column(modifier = Modifier.fillMaxSize()) {
            Box(modifier = Modifier.weight(1f)) {
                if (hasLocationPermission) {
                    if (location != null) {
                        mapComponent(location!!)
                    } else {
                        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                            CircularProgressIndicator()
                        }
                    }
                }
                // Profile button overlay — top-right corner
                if (FeatureFlags.LOGIN_ENABLED) {
                    IconButton(
                        onClick = onProfileClick,
                        modifier = Modifier
                            .align(Alignment.TopEnd)
                            .padding(8.dp)
                            .zIndex(1f)
                    ) {
                        Icon(
                            imageVector = Icons.Filled.AccountCircle,
                            contentDescription = "Profile",
                            tint = MaterialTheme.colorScheme.onSurface
                        )
                    }
                }
            }
            // CenterOnUserButton(getLocation = locationService::getCurrentLocation, onLocationFound = onUserLocationFound, onError = {})
            if (shareServiceIsSupported()) {
                Button(
                    modifier = Modifier.align(alignment = Alignment.CenterHorizontally),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.primary,
                        contentColor = MaterialTheme.colorScheme.onPrimary
                    ),
                    onClick = {
                        shareLocation()
                    }) {
                    Text(text = "Share My Location")
                }
            }
        }

        if (location != null) {
            onUserLocationFound(location!!.latitude, location!!.longitude)
        }

        LaunchedEffect(track) {
            if (track.isNotEmpty()) {
                onTrackAvailable(track)
            }
        }
    }
}

@Composable
fun CenterOnUserButton(
    getLocation: suspend () -> LocationResult,
    onLocationFound: (Double, Double) -> Unit,
    onError: (String) -> Unit,
) {
    val scope = rememberCoroutineScope()

    Button(
        onClick = {
            scope.launch {
                when (val result = getLocation()) {
                    is LocationResult.Success -> {
                        onLocationFound(
                            result.location.latitude,
                            result.location.longitude
                        )
                    }
                    is LocationResult.Error -> {
                        onError(result.reason.toString())
                    }
                }
            }
        }
    ) {
        Text("Use my location")
    }
}

@Preview(showBackground = true)
@Composable
fun HomeScreenContentPreview() {
    MaterialTheme {
        HomeScreenContent(
            hasLocationPermission = true,
            location = GenericLocation(latitude = 55.6761, longitude = 12.5683),
            mapComponent = { _ ->
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .background(MaterialTheme.colorScheme.surfaceVariant),
                    contentAlignment = Alignment.Center
                ) {
                    Text("Map Placeholder")
                }
            },
            onProfileClick = {},
            shareServiceIsSupported = { true },
            shareLocation = {},
            onUserLocationFound = { _, _ -> },
            track = emptyList(),
            onTrackAvailable = {},
        )
    }
}